import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./SignupModal.css";

interface SignupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SignupModal: React.FC<SignupModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: "",
    companyName: "",
    city: "",
    state: "",
    requestDemo: false,
    subscribeNewsletter: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // HubSpot Partner ID
  const partnerId = "48197990";

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsSubmitted(false);
      setFormData({
        email: "",
        companyName: "",
        city: "",
        state: "",
        requestDemo: false,
        subscribeNewsletter: false,
      });
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Send tracking event to HubSpot
    if ((window as any)._hsq) {
      (window as any)._hsq.push([
        "identify",
        {
          email: formData.email,
          company: formData.companyName,
          city: formData.city,
          state: formData.state,
          request_demo: formData.requestDemo ? "Yes" : "No",
          newsletter_subscription: formData.subscribeNewsletter ? "Yes" : "No",
        },
      ]);

      (window as any)._hsq.push([
        "trackEvent",
        {
          id: "early_access_signup",
          value: partnerId,
        },
      ]);
    }

    // Simulate API call to save the data
    setTimeout(() => {
      console.log("Form submitted:", formData);
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 1500);
  };

  // Handle close button click
  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    onClose();
  };

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="signup-modal early-access-modal gesslar-signup-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="modal-close-btn"
          onClick={handleClose}
          aria-label="Close modal"
        >
          <span aria-hidden="true">&times;</span>
          <span className="sr-only">Close</span>
        </button>

        <div className="modal-content">
          <h2>{t("signup.title")}</h2>
          <p>{t("signup.subtitle")}</p>

          {isSubmitted ? (
            <div className="success-message">
              <h3>{t("signup.success.title")}</h3>
              <p>{t("signup.success.message")}</p>
              <button className="btn btn-primary" onClick={onClose}>
                {t("signup.success.close", "Close")}
              </button>
            </div>
          ) : (
            <form
              className="signup-form early-access-form gesslar-signup-form platform-signup-form"
              onSubmit={handleSubmit}
            >
              <div className="form-group">
                <label htmlFor="email">{t("signup.form.email")}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required 
                  placeholder={t('signup.placeholders.email')} 
                />
              </div>
              <div className="form-group">
                <label htmlFor="companyName">{t("signup.form.company")}</label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required 
                  placeholder={t('signup.placeholders.company')} 
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">{t("signup.form.city")}</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required 
                    placeholder={t('signup.placeholders.city')} 
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="state">{t("signup.form.state")}</label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required 
                    placeholder={t('signup.placeholders.state')} 
                  />
                </div>
              </div>

              <div className="form-checkbox">
                <input
                  type="checkbox"
                  id="requestDemo"
                  name="requestDemo"
                  checked={formData.requestDemo}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="requestDemo">{t('signup.form.requestDemo')}</label>
              </div>

              <div className="form-checkbox">
                <input
                  type="checkbox"
                  id="subscribeNewsletter"
                  name="subscribeNewsletter"
                  checked={formData.subscribeNewsletter}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="subscribeNewsletter">{t('signup.form.subscribeNewsletter')}</label>
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={isSubmitting}
              >
                {isSubmitting ? t("contact.loading") : t("signup.form.submit")}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignupModal;
